import React from 'react';

import { createPortal } from 'react-dom';

import { createQueryClient, QueryClientProvider, ReactQueryDevtools } from '@lumapps/base-api/react-query';
import { getDefaultInstanceLanguage, getInstanceLanguages } from '@lumapps/instance/ducks/selectors';
import { ErrorBoundary, ErrorBoundaryProps } from '@lumapps/lumx-suspense/components/ErrorBoundary';
import { Provider, Store, useSelector } from '@lumapps/redux/react';
import { BrowserRouter, Context } from '@lumapps/router';
import { RouterProps } from '@lumapps/router/routers';
import { TranslationsProvider, TranslationConfig } from '@lumapps/translations';

/** Add html attribute on element focus by keyboard (required for LumX focus style). */
import 'focus-visible';

declare const window: any;

const queryClient = createQueryClient();

interface TranslationsShellProps {
    children: React.ReactNode;
    /** base URL for the routing */
    baseUrl: string;
    /** list of translations */
    translationsState: TranslationConfig;
    /** props for the router context */
    routerProps?: RouterProps;
}

export interface ShellProps extends TranslationsShellProps {
    /** redux store */
    store: Store<BaseStore>;
    /** custom properties for the error boundary */
    errorBoundaryProps?: Partial<ErrorBoundaryProps>;
    /** whether we are on a dev environment or not. */
    isDevEnv?: boolean;
}

const TranslationsShell: React.FC<TranslationsShellProps> = ({
    baseUrl,
    children,
    translationsState,
    routerProps = {},
}) => {
    const langs = useSelector(getInstanceLanguages);
    const defaultLang = useSelector(getDefaultInstanceLanguage);

    return (
        <TranslationsProvider value={translationsState} instanceDefaultLanguage={defaultLang} instanceLanguages={langs}>
            <Context.Provider value={routerProps}>
                <BrowserRouter basename={baseUrl}>{children}</BrowserRouter>
            </Context.Provider>
        </TranslationsProvider>
    );
};

/**
 * Component that wraps any component with necessary providers and configurations.
 * @param ShellProps
 * @returns Shell
 */
export const Shell: React.FC<ShellProps> = ({
    baseUrl,
    store,
    children,
    translationsState,
    errorBoundaryProps,
    isDevEnv,
    routerProps = {},
}) => {
    if (window.lumappsPerformances) {
        window.lumappsPerformances.loaderHidden = Date.now();
    }

    return (
        <ErrorBoundary {...errorBoundaryProps}>
            <QueryClientProvider client={queryClient}>
                <Provider store={store}>
                    <TranslationsShell
                        baseUrl={baseUrl}
                        translationsState={translationsState}
                        routerProps={routerProps}
                    >
                        {children}
                    </TranslationsShell>
                </Provider>

                {/**
                 * ℹ️ Even though Devtools are excluded in production builds (@see https://tanstack.com/query/latest/docs/react/devtools#devtools-in-production)
                 * avoid creating an unnecessary portal in production builds
                 */}
                {isDevEnv && createPortal(<ReactQueryDevtools />, document.body)}
            </QueryClientProvider>
        </ErrorBoundary>
    );
};
